import { Search, X } from 'lucide-react';

import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group';
import type { SitrepSectionType } from 'components/DisasterGPT/hooks/useSitrepsV2';

type ReportContentSearchProps = {
  value: string;
  onChange: (value: string) => void;
};

/** Left-aligned search shown above a report's first section. */
export function ReportContentSearch({
  value,
  onChange,
}: ReportContentSearchProps) {
  return (
    <div className="tw:mb-3 tw:flex tw:justify-start">
      <InputGroup className="tw:w-full tw:max-w-sm">
        <InputGroupAddon>
          <Search />
        </InputGroupAddon>
        <InputGroupInput
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="Search report..."
          aria-label="Search report"
        />
        {value ? (
          <InputGroupAddon align="inline-end">
            <InputGroupButton
              size="icon-xs"
              aria-label="Clear report search"
              onClick={() => onChange('')}
            >
              <X />
            </InputGroupButton>
          </InputGroupAddon>
        ) : null}
      </InputGroup>
    </div>
  );
}

const sectionSearchText = (section: SitrepSectionType) =>
  [
    section.name,
    section.ai_instructions,
    ...(section.section_versions ?? []).flatMap((version) => [
      version.data_string,
      version.sitrep_section_text,
    ]),
  ]
    .filter(Boolean)
    .join('\n')
    .toLowerCase();

/** Keep a section when its name or saved text matches, or a nested child does. */
export function sectionMatchesReportSearch(
  section: SitrepSectionType,
  query: string,
  children: SitrepSectionType[] = []
) {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return true;
  if (sectionSearchText(section).includes(trimmed)) return true;
  return children.some((child) => sectionSearchText(child).includes(trimmed));
}
